import React, { memo, useCallback } from 'react';
import { OverlayProps, Modal, Button } from '~/shared/components';
import { useBasket } from '../../hooks';
import {
    StyledMiniBasketItems,
    StyledMiniBasketActions,
    StyledMiniBasketHeadline,
    StyledMiniBasketSummarize,
    StyledFreeFreightContainer,
} from './styled';
import Check from '$icons/check.svg';
import { useFrame } from '~/shared/utils';
import { useTranslation } from '~/shared/utils/translation';
import { MiniBasketItem } from './components/MiniBasketItem';
import { FreeShippingProgress } from '../FreeShippingProgress';
import { weakKey } from '~/shared/utils/jsx';
import { Summary } from '~/features/checkout';
import { AnimatePresence } from 'framer-motion';
import { SizeCarousel } from '~/shared/components/size-carousel/size-carousel.component';
export type MiniBasketProps = Partial<OverlayProps>;

export const MiniBasket = memo(() => {
    const { data: basket, isMiniBasketOpen, toggleMiniBasket, totalQuantity } = useBasket();
    const { data: frameData } = useFrame();
    const { translate } = useTranslation();
    const staticLinks = frameData?.staticLinks;

    const closeMiniBasket = useCallback(() => toggleMiniBasket(false), [toggleMiniBasket]);

    return (
        <Modal
            isOpen={isMiniBasketOpen}
            variant="drawerRight"
            onClose={closeMiniBasket}
            darkHeader
            removeBorders
            headline={
                <StyledMiniBasketHeadline>
                    <Check />
                    {translate('minibasket.headline')}
                </StyledMiniBasketHeadline>
            }
            footer={
                <>
                    <StyledMiniBasketSummarize>
                        <StyledFreeFreightContainer>
                            <FreeShippingProgress variant="minimal" />
                        </StyledFreeFreightContainer>
                        {basket && <Summary sum={basket} variant="minimal" />}
                    </StyledMiniBasketSummarize>
                    <div>
                        {frameData?.settings?.features?.carouselContextId && (
                            <SizeCarousel
                                headline={translate('minibasket.carouselHeadline')}
                                buttonText={translate('minibasket.carouselCardButtonText')}
                                contextId={frameData.settings.features.carouselContextId}
                                trackingEvent="trackMiniBasketAddToBasket"
                            />
                        )}
                    </div>
                    <StyledMiniBasketActions>
                        <Button
                            variant="secondary"
                            onClick={closeMiniBasket}
                            size="medium"
                            centeredText
                        >
                            {translate('minibasket.continueButton')}
                        </Button>
                        {staticLinks?.basketPage?.url && (
                            <Button
                                centeredText
                                href={staticLinks.basketPage.url}
                                size="medium"
                                onClick={closeMiniBasket}
                            >
                                {translate('minibasket.goTobasketButton', {
                                    quantity: totalQuantity || '0',
                                })}
                            </Button>
                        )}
                    </StyledMiniBasketActions>
                </>
            }
        >
            <StyledMiniBasketItems>
                <AnimatePresence initial={false}>
                    {[...(basket?.lines ?? [])]?.reverse()?.map((line) => (
                        <MiniBasketItem {...line} key={weakKey(line)} />
                    ))}
                </AnimatePresence>
            </StyledMiniBasketItems>
        </Modal>
    );
});
